import axios from "axios";

import { executeGet } from "../../../../react/core/utils/apiUtils";
import { DEFAULT_MESSAGE } from "../../../../react/core/utils/errorUtils";
import ObjectUtil from "../../../../react/core/utils/ObjectUtil";
import {
    isLocalhost,
    hasMocks,
    localPcapApi,
    pcapBundleEnv,
    getParameterByName
} from "../../../../react/core/utils/urlUtils";
import ExternalLogger from "../logger/ExternalLogger";
import { PaeConstants } from "../pae/PaeConstants";

const logger = ExternalLogger.getInstance("IntegratedEligibilityService");
const PCAP_LOCAL_HOST = `${__HTTP_PREFIX__}://localhost:${__LOGIN_UI_PORT__}`;

const getGlobalValues = () => globalThis.integratedEligibility;

const postSamlResponse = async (samlToken) => {
    if (ObjectUtil.isUndefinedOrNull(samlToken)) {
        return { csrf: null, success: false, userGuid: null };
    }

    const mock = window.location.origin + "/participant/home/data/post-pc-api-saml2.json";

    const pcApiDev = "https://pc-api-dev.retirementpartner.com/api/empower/sso/saml2";
    const { postOriginUrl } = globalThis.integratedEligibility;
    const liveUrl = ObjectUtil.isUndefinedOrNull(postOriginUrl)
        ? pcApiDev
        : `${postOriginUrl}/api/empower/sso/saml2`;

    let url = isLocalhost() && hasMocks() ? mock : liveUrl;

    if (localPcapApi && !hasMocks()) {
        url = `${PCAP_LOCAL_HOST}/api/empower/sso/saml2`;
    }

    const formData = new FormData();
    formData.append("SAMLResponse", samlToken);

    try {
        const { data } = await axios.post(url, formData);
        return data?.spHeader;
    } catch (error) {
        const samlTokenError = {
            message: DEFAULT_MESSAGE,
            code: "SAMLTKN",
            error: true
        };
        globalThis.integratedEligibility.samlTokenError = samlTokenError;
        return error;
    }
};

const handleEligibilityServiceResponse = (data, currentStoredData) => {
    const usingLocalMocks = isLocalhost() && hasMocks();

    globalThis.integratedEligibility = {
        ...currentStoredData,
        ...data.data,
        eligibilityLoaded: true,
        groupDetails: data.logInfo,
        notifications: data.notifications
    };

    if (!globalThis.integratedEligibility.setEmulator) {
        if (usingLocalMocks) {
            globalThis.integratedEligibility.isEmulator = globalThis.forceEmulator || false;
            globalThis.integratedEligibility.setEmulator = true;
        } else {
            globalThis.integratedEligibility.isEmulator =
                globalThis.forceEmulator || data.data.type !== PaeConstants.PARTICIPANT; // PAE | PRIVILEGE
            globalThis.integratedEligibility.setEmulator = true;
        }
    }

    if (localPcapApi) {
        globalThis.integratedEligibility.postOriginUrl = PCAP_LOCAL_HOST;
    }

    if (pcapBundleEnv) {
        let pcapEnv = getParameterByName("pcapBundle");

        if (pcapEnv === "prod") {
            pcapEnv = "";
        } else {
            pcapEnv = "-" + pcapEnv;
        }

        const pcapEnvUrl = "pc-api" + pcapEnv;

        const baseUrl =
            window.location.protocol +
            "//" +
            pcapEnvUrl +
            "." +
            window.location.host.split(".").slice(1).join(".");
        const versionPath = "/static/bin/version.json";

        $.ajax({
            url: baseUrl + versionPath,
            success: function (response) {
                logger.info("OVERRIDING PCAP BUNDLES");
                globalThis.integratedEligibility.integratedJS = `${baseUrl}/static/js/empower.bundle${response.version}.js`;
                globalThis.integratedEligibility.integratedCSS = `${baseUrl}/static/css/empower/app/main${response.version}.css`;
            },
            dataType: "json"
        });
    }

    if (data.data.integrated && ObjectUtil.isUndefinedOrNull(data.data.samlToken)) {
        logger.error("IntegratedEligibilityService - NO SAML TOKEN - bad data :>> ", data);
        const samlTokenError = {
            message: DEFAULT_MESSAGE,
            code: "SAMLTKN",
            error: true
        };
        if (data.notifications.length > 0) {
            let isWarning = data.notifications[0].type === "WARNING";
            let isTerminated =
                String(data.notifications[0].message).indexOf("terminated with no Balance") > -1;
            if (isWarning && isTerminated) {
                samlTokenError.code = "LIAT_CODE1";
            }
        }
        globalThis.integratedEligibility.samlTokenError = samlTokenError;

        return Promise.reject(data);
    }

    if (
        data.data.integrated &&
        data.notifications &&
        data.notifications.length > 0 &&
        data.notifications[0].type === "ERROR"
    ) {
        // PPT is redirected to AOD
        return Promise.reject({
            message: DEFAULT_MESSAGE,
            showError: true
        });
    }

    return data.data;
};

const IntegratedEligibilityService = async function () {
    const liveUrl = __baseApiUrl__ + "/participant-web-services/rest/home/eligibility";
    const mock = window.location.origin + "/participant/home/data/get-integrated-eligibility.json";
    const usingLocalMocks = isLocalhost() && hasMocks();
    const url = usingLocalMocks ? mock : liveUrl;

    const storedValues = getGlobalValues();

    if (storedValues.eligibilityLoaded) {
        return Promise.resolve(storedValues);
    } else {
        try {
            globalThis.integratedEligibility.eligibilityLoaded = "in_progress";

            const { data } = await executeGet(url, "getIntegratedEligibilityFault");
            if (ObjectUtil.isUndefinedOrNull(data)) {
                console.warn("bad data :>> ", data);
                return Promise.resolve(storedValues);
            }

            globalThis.integratedEligibility = {
                ...storedValues,
                ...data.data,
                eligibilityLoaded: true,
                groupDetails: data.logInfo,
                notifications: data.notifications
            };

            if (!globalThis.integratedEligibility.setEmulator) {
                if (usingLocalMocks) {
                    globalThis.integratedEligibility.isEmulator = globalThis.forceEmulator || false;
                    globalThis.integratedEligibility.setEmulator = true;
                } else {
                    globalThis.integratedEligibility.isEmulator =
                        globalThis.forceEmulator || data.data.type !== PaeConstants.PARTICIPANT; // PAE | PRIVILEGE
                    globalThis.integratedEligibility.setEmulator = true;
                }
            }

            if (localPcapApi) {
                globalThis.integratedEligibility.postOriginUrl = PCAP_LOCAL_HOST;
            }

            if (pcapBundleEnv) {
                let pcapEnv = getParameterByName("pcapBundle");

                if (pcapEnv === "prod") {
                    pcapEnv = "";
                } else {
                    pcapEnv = "-" + pcapEnv;
                }

                const pcapEnvUrl = "pc-api" + pcapEnv;

                const baseUrl =
                    window.location.protocol +
                    "//" +
                    pcapEnvUrl +
                    "." +
                    window.location.host.split(".").slice(1).join(".");
                const versionPath = "/static/bin/version.json";

                $.ajax({
                    url: baseUrl + versionPath,
                    success: function (response) {
                        logger.info("OVERRIDING PCAP BUNDLES");
                        globalThis.integratedEligibility.integratedJS = `${baseUrl}/static/js/empower.bundle${response.version}.js`;
                        globalThis.integratedEligibility.integratedCSS = `${baseUrl}/static/css/empower/app/main${response.version}.css`;
                    },
                    dataType: "json"
                });
            }

            if (data.data.integrated && ObjectUtil.isUndefinedOrNull(data.data.samlToken)) {
                console.warn("NO TOKEN bad data :>> ", data);
                const samlTokenError = {
                    message: DEFAULT_MESSAGE,
                    code: "SAMLTKN",
                    error: true
                };
                if (data.notifications.length > 0) {
                    let isWarning = data.notifications[0].type === "WARNING";
                    let isTerminated =
                        String(data.notifications[0].message).indexOf(
                            "terminated with no Balance"
                        ) > -1;
                    if (isWarning && isTerminated) {
                        samlTokenError.code = "LIAT_CODE1";
                    }
                }
                globalThis.integratedEligibility.samlTokenError = samlTokenError;

                return Promise.reject(data);
            }

            if (
                data.data.integrated &&
                data.notifications &&
                data.notifications.length > 0 &&
                data.notifications[0].type === "ERROR"
            ) {
                // PPT is redirected to AOD
                return Promise.reject({
                    message: DEFAULT_MESSAGE,
                    showError: true
                });
            }
            return data.data;
        } catch (e) {
            console.warn("core-IntegratedEligibilityService catch e :>> ", e);
            if (e.data) {
                globalThis.integratedEligibility.integrated = ObjectUtil.isUndefinedOrNull(
                    e.data.integrated
                )
                    ? false
                    : e.data.integrated;
                globalThis.integratedEligibility.homePageEligibility = ObjectUtil.isUndefinedOrNull(
                    e.data.homePageEligibility
                )
                    ? false
                    : e.data.homePageEligibility;
            }
            return await Promise.resolve(storedValues);
        }
    }
};

export {
    IntegratedEligibilityService,
    getGlobalValues,
    postSamlResponse,
    handleEligibilityServiceResponse
};
